import React from 'react';
import { connect } from 'react-redux';

import Modal from 'react-modal';
import { Creatable } from 'react-select';

import Analytics from 'APP_ROOT/services/analytics';
import { strings } from 'APP_ROOT/localization';
import { ModalLoadingPage } from 'APP_ROOT/components/common/loadingPage';
import ConfirmDialog from 'APP_ROOT/components/common/ConfirmDialog';
import SimpleProfileImage from 'APP_ROOT/components/SimpleProfileImage';
import identities from 'APP_ROOT/data/identities';

import FaceImageGallery from './FaceImageGallery'

const KeyCode = {
    TAB: 9,
    ENTER: 13,
    ESCAPE: 27,
};

export class FaceDetailModal extends React.Component {

    constructor(props) {
        super(props);
        this.form = React.createRef();
        this.state = {
            selectedOption: '',
            nameValidationError: '',
        }
    }

    componentDidMount() {
        this.loadIdentity();
    }

    componentDidUpdate(prevProps) {
        const { identity, identityId } = this.props;
        // if this is the first time we've gotten identity in props and it has a name,
        // set the selected name
        if (identity && (!prevProps.identity || identity.name !== prevProps.identity.name)) {
            this.setState({
                selectedOption: identity.name ? { 
                    value: identity.name,
                    label: identity.name
                } : null
            });
        }
        if (identityId !== prevProps.identityId) {
            this.loadIdentity();
        }
    }

    loadIdentity = () => {
        const { identityId, loadDetailedIdentity, user: { stage } } = this.props;
        if (identityId) {
            loadDetailedIdentity(stage, identityId);
        }
    }

    onAfterOpen = () => {
        Analytics.modalView('/FaceDetailModal');
    }

    closeModal = (e) => {
        e && e.preventDefault();
        this.setState({
            nameValidationError: ''
        });
        this.props.onRequestClose();
    }

    validateForm() {
        const { selectedOption } = this.state;
        if(!selectedOption) {
            this.setState({ nameValidationError: strings.new_faces_name_validation_error_name_is_required });
            return false;
        }
        return true;
    }

    onFormSubmit = (e) => {
        e.preventDefault();
        const { onRequestSetName, identityId } = this.props;
        if (this.validateForm()) {
            onRequestSetName(identityId, this.state.selectedOption.label);
        }
    }

    confirmMergeIdentity = (identityId, name) => {
        this.setState({
            confirmMergeIdentityData: {
                identityId,
                title: strings.faces_merge_confirmation_title,
                message: strings.formatString(strings.faces_merge_confirmation_you_are_about_merge_this_identity_into_x, name)
            }
        });
    }

    cancelMergeIdentity = () => {
        this.setState({
            confirmMergeIdentityData: ''
        });
    }

    mergeIdentity = (mainIdentityId) => {
        const thisIdentityId = this.props.identity.identityId;
        this.props.mergeIdentities(mainIdentityId, thisIdentityId);
        this.setState({
            confirmMergeIdentityData: ''
        });
    }

    onNameChange = (selectedOption) => {
        // selectedOption can be null when the `x` (close) button is clicked
        if (!selectedOption) {
            this.setState({
                selectedOption:'',
                nameValidationError: strings.new_faces_name_validation_error_name_is_required
            });
            return;
        }

        if (selectedOption.identityId &&
            selectedOption.identityId !== this.props.identity.identityId) {
            this.confirmMergeIdentity(selectedOption.identityId, selectedOption.label);
        } else {
            this.setState({
                selectedOption,
                nameValidationError:''
            });
        }
    }

    onKeyDownInSelect = (event) => {
        switch (event.keyCode) {
            case KeyCode.ESCAPE:
                event.preventDefault();
                this.closeModal();
                break;
            case KeyCode.TAB:
                break;
            case KeyCode.ENTER:
                break;
            default:
                break;
        }
    }

    onNameFieldBlur = (event) => {
        const value = event.target.value;
        if (value) {
            this.setState({
                selectedOption: { 
                    value: value,
                    label: value
                }
            });
        }
        if(!event.relatedTarget || !event.relatedTarget.form) {
            this.form.current.focus();
        }
    }

    setAsProfile = faceId => {
        const { identity, setIdentityProfileFace } = this.props;
        return setIdentityProfileFace(identity.identityId, faceId);
    }

    render() {

        const {
            identity,
            loadingDetailedIdentity,
            onRequestDeleteImages,
            onRequestUnassignImages,
            progressMessage,
            nameOptions,
        } = this.props;

        const {
            selectedOption,
            nameValidationError,
            confirmMergeIdentityData,
        } = this.state;

        let nameError = null;
        if (nameValidationError) {
            nameError = <p className='error-message'><i className='material-icons'>warning</i>{nameValidationError}</p>
        }

        const progressView = progressMessage
            ? <div className='loading-overlay'><ModalLoadingPage message={progressMessage} /></div>
            : null;

        return (
            <Modal
                isOpen={!!identity}
                contentLabel='face-detail'
                className='react-modal-content face-detail-modal'
                htmlOpenClassName='ReactModal__Html--open'
                overlayClassName='react-modal-overlay'
                onRequestClose={this.closeModal}
                onAfterOpen={this.onAfterOpen}>

                { identity &&
                    <div className='container-fluid form-card'>
                        <form onSubmit={this.onFormSubmit} ref={this.form}>
                            <div className='d-flex flex-row'>
                                <SimpleProfileImage.Small src={identity.profileImageUrl} margin='0 0.5rem 0 0' />
                                <div className='d-flex flex-column flex-grow-1'>
                                    <Creatable
                                        autoFocus={true}
                                        autoBlur={true}
                                        escapeClearsValue={false}
                                        placeholder={strings.new_faces_name_placeholder}
                                        options={nameOptions}
                                        value={selectedOption}
                                        name='face-name'
                                        onChange={this.onNameChange}
                                        onInputKeyDown={this.onKeyDownInSelect}
                                        onBlur={this.onNameFieldBlur}
                                        onBlurResetsInput={false}
                                        onCloseResetsInput={false}
                                        promptTextCreator={ label => strings.new_faces_name_prompt_text_creator + label }
                                        noResultsText={strings.new_faces_no_preexisting_names}
                                        optionRenderer={this.renderOption}
                                    />
                                    {nameError}
                                </div>
                            </div>
                            { loadingDetailedIdentity && !(identity.faces && identity.faces.length) ? (
                                <div className='ml-3 loading-spinner spinner-border' role='status'>
                                    <span className="sr-only">{strings.loading}</span>
                                </div>
                            ) : (
                                <FaceImageGallery
                                    images={identity.faces}
                                    onRequestDeleteImages={onRequestDeleteImages}
                                    onRequestUnassignImages={onRequestUnassignImages}
                                    setAsProfile={this.setAsProfile}
                                />
                            )}
                            <div className='row'>
                                <div className='col-12 action-btns text-right'>
                                    <button className='btn btn-outline-blue' onClick={this.closeModal}>
                                        <i className='material-icons'>close</i>
                                        {strings.formatString(strings.faces_close)}
                                    </button>
                                    <button className='btn'>
                                        <i className='material-icons'>check</i>
                                        {strings.formatString(strings.faces_save)}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                }
                <ConfirmDialog
                    isOpen={!!confirmMergeIdentityData}
                    contentLabel='confirm-merge-identity'
                    onOk={() => this.mergeIdentity(confirmMergeIdentityData.identityId)}
                    onCancel={this.cancelMergeIdentity}
                    onDismiss={this.cancelMergeIdentity}
                    {...confirmMergeIdentityData}
                />
                {progressView}
            </Modal>

        );
    }

    renderOption = option => {
        return <FaceItem option={option} identity={this.props.identity} />
    }

}

const FaceItem = ({ option, identity }) => (
    <div className='d-flex flex-row align-items-center'>
        <SimpleProfileImage.Small width={36} src={option.profileImageUrl || identity.profileImageUrl} />
        <div className="ml-1">{option.label}</div>
        {
            (option.profileImageUrl && option.label !== identity.name) &&
                <div className="ml-1 badge badge-secondary">{strings.faces_merge_badge}</div>
        }
    </div>
);

const mapStateToProps = (state, ownProps) => {
    const { identityId } = ownProps;
    return {
        identity: identities.Selectors.identity(state, identityId),
        loadingDetailedIdentity: identities.Selectors.loadingDetailedIdentity(state, identityId),
        nameOptions: identities.Selectors.nameOptions(state),
    };
};

const mapDispatchToProps = dispatch => {
    return {
        loadDetailedIdentity: (stage, identityId) => dispatch(identities.Actions.loadDetailedIdentity(stage, identityId))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FaceDetailModal);
